import {
  MutableRefObject,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import _ from 'lodash';
import {
  Dialog,
  DialogActions,
  DialogContent,
  Typography,
  useMediaQuery,
  useTheme,
  Box,
} from '@mui/material';
import {t} from 'i18next';
import {GridRowParams} from '@mui/x-data-grid';
import {
  ActionType,
  SelectedListType,
  useEvidenceSelectionReducer,
} from './reducer';
import {AurTextField} from '../../../../common/aur-text-field/AurTextField';
import {RootState} from '../../../../../redux/store';
import {AurDataGridColDef} from '../../../../../types/types';
import AurDataGrid from '../../../../common/aur-data-grid/AurDataGrid';
import AurPagination from '../../../../common/aur-pagination/AurPagination';
import {ReactComponent as RadioCheckedIcon} from '../../../../../assests/svg/radio-checked.svg';
import {ReactComponent as RadioUnCheckedIcon} from '../../../../../assests/svg/radio-unchecked.svg';
import {TABLE_PAGE_SIZE_OPTION} from '../../../../../constants/constants';
import AurDataGridErrorOverlay from '../../../../common/aur-data-grid-error-overlay/AurDataGridErrorOverlay';
import AurDataGridNoRowsOverlay from '../../../../common/aur-data-grid-no-rows-overlay/AurDataGridNoRowsOverlay';
import AurDataGridLoadingOverlay from '../../../../common/aur-data-grid-loading-overlay/AurDataGridLoadingOverlay';
import {setMediaEvidenceId} from '../../../../../redux/slices/reportsSlice';
import {getInprogressEvidenceSourcesByWorkspaceId} from '../../../../../api/ReportingManagement';
import AurSearchBox from '../../../../common/aur-search-box/AurSearchBox';
import {
  GA_CLICK_TYPES,
  GA_DATA_MODE_AUTO,
  GA_EVENT_NAMES,
  GA_REGIONS,
  GA_SUBPAGE_TYPE,
} from '../../../../../constants/analyticsConstants';
import {useAnalytics} from '../../../../../hooks/useAnalytics';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  onContinue: () => void;
  disablePortal?: boolean; // Use it for jest test case only
}

const mapColumnHeaderName = (type: SelectedListType | null) => {
  switch (type) {
    case 'PROJECT':
      return 'reports.processingInProgress.evidenceSourceDialog.header.project';
    case 'EVIDENCE_SOURCE':
      return 'reports.processingInProgress.evidenceSourceDialog.header.evindence';
    case 'WORKSPACE':
      return 'reports.processingInProgress.evidenceSourceDialog.header.workspace';
    default:
      return '';
  }
};

const getTableAriaLabel = (type: SelectedListType | null) => {
  switch (type) {
    case 'PROJECT':
      return t('accessibility.table.projectList');
    case 'WORKSPACE':
      return t('accessibility.table.wokrspaceList');
    case 'EVIDENCE_SOURCE':
      return t('accessibility.table.evidenceSourceList');
    default:
      return '';
  }
};
const COMMON_STRING_PATH = 'reports.processingInProgress.evidenceSourceDialog.';
const COLUMN_HEIGHT = 64;
const ROW_HEIGHT = 56;

const EmptyDiv = () => {
  return <div />;
};

const EvidenceSelectionDialog: React.FC<Props> = ({
  isOpen,
  onClose,
  onContinue,
  disablePortal = false,
}) => {
  const tableContainerRef = useRef<HTMLDivElement | null>(null);
  const projectButtonRef = useRef<HTMLInputElement | null>(null);
  const workspaceButtonRef = useRef<HTMLInputElement | null>(null);
  const evidenceButtonRef = useRef<HTMLInputElement | null>(null);

  const theme = useTheme();
  const dispatch = useDispatch();
  const {trackGAEvent} = useAnalytics();
  const {
    selectedProjectName,
    selectedWorkSpaceName,
    projectId,
    workspaceId,
    mediaEvidenceId,
    mediaEvidenceName,
    clientId,
    previousReportTypeTab,
  } = useSelector((state: RootState) => state.reports);

  const [focusedTextField, setFocusedTextField] =
    useState<SelectedListType | null>(null);
  const fullScreen = useMediaQuery(theme.breakpoints.down('md'));
  const evidenceLoadedForWorkspaceId = useRef<string | null>(null);
  const state = useEvidenceSelectionReducer();
  const {
    selectedListType,
    selectedRowItem,
    tableRows,
    reqLoading,
    haveReqError,
    selectedProject,
    selectedWorkspace,
    selectedEvidenceSource,
    searchQuery,
    currentPageNo,
    noOfItemPerPage,
    enableContinueButton,
    totalRowCount,
    evidenceSearchLoading,
    setSelectedListType,
    setValue,
  } = state;

  const updatedStateRef = useRef(state);

  useEffect(() => {
    isOpen &&
      trackGAEvent({
        eventName: GA_EVENT_NAMES.SUB_PAGE_VIEW,
        subPage: {
          subpage_name: t('gaTracking.reports.evidenceSourceDialog.title'),
          subpage_type: GA_SUBPAGE_TYPE.MODAL,
        },
        user: GA_DATA_MODE_AUTO,
      });
  }, [isOpen]);

  useEffect(() => {
    /**
     * Focusing input field when list type change automatically on selection of option from list
     */
    switch (selectedListType) {
      case 'PROJECT':
        if (projectButtonRef.current) projectButtonRef.current?.focus();
        else {
          /**
           * On First time moumt, UI some milisecond to build UI and this effect got executed for it so
           * getting ref null. By adding timeout we make sure project button ref exist
           */
          setTimeout(() => {
            projectButtonRef.current?.focus();
          }, 500);
        }
        break;
      case 'WORKSPACE':
        workspaceButtonRef.current?.focus();
        break;
      case 'EVIDENCE_SOURCE':
        if (evidenceButtonRef.current) {
          evidenceButtonRef.current.focus();
        } else {
          setTimeout(() => {
            evidenceButtonRef.current?.focus();
          }, 500);
        }
        break;
    }
  }, [selectedListType, isOpen]);

  useEffect(() => {
    if (isOpen && setValue && projectId && selectedProjectName) {
      if (selectedWorkSpaceName && workspaceId) {
        setValue(ActionType.SET_INITIAL_DATA, {
          projectName: selectedProjectName,
          projectId: projectId,
          workspaceName: selectedWorkSpaceName,
          workspaceId: workspaceId,
          mediaEvidenceId: mediaEvidenceId,
          mediaEvidenceName: mediaEvidenceName,
        });
      } else {
        setValue(ActionType.SET_INITIAL_DATA, {
          projectName: selectedProjectName,
          projectId: projectId,
        });
      }
    }
  }, [isOpen]);

  useEffect(() => {
    updatedStateRef.current = state;
  }, [state]);

  useEffect(() => {
    if (isOpen && clientId) {
      setValue(ActionType.SET_SEARCH_TERM, '');
    }
  }, [isOpen, clientId]);

  useEffect(() => {
    const {selectedListType, selectedWorkspace, searchQuery} =
      updatedStateRef.current;
    if (selectedListType === 'EVIDENCE_SOURCE' && selectedWorkspace) {
      fetchEvidenceSources(searchQuery === '' ? undefined : searchQuery);
    }
  }, [noOfItemPerPage, currentPageNo]);

  useEffect(() => {
    if (
      selectedListType === 'EVIDENCE_SOURCE' &&
      selectedWorkspace &&
      selectedWorkspace.id !== evidenceLoadedForWorkspaceId.current
    ) {
      fetchEvidenceSources();
    }
  }, [selectedListType, selectedWorkspace]);

  useEffect(() => {
    evidenceLoadedForWorkspaceId.current = null;
  }, [selectedProject]);

  const perPageOptions = useMemo(() => {
    const options = TABLE_PAGE_SIZE_OPTION.filter(
      (option) => option <= totalRowCount
    );
    const additionalOption = TABLE_PAGE_SIZE_OPTION.find(
      (option) => option >= totalRowCount
    );
    if (additionalOption && !options.includes(additionalOption)) {
      options.push(additionalOption);
    }
    return options;
  }, [tableRows, totalRowCount]);

  const tableColumns: AurDataGridColDef[] = useMemo(() => {
    const columns: AurDataGridColDef[] = [
      {
        field: 'name',
        headerName: t(mapColumnHeaderName(selectedListType)),
        flex: 0.25,
        sortable: false,
        renderCell: (params) => (
          <div
            className="row-container"
            data-testid={`row-${params.id}`}
            tabIndex={params.tabIndex}
          >
            <div className="img-container">
              {params.id === selectedRowItem?.id ? (
                <RadioCheckedIcon data-testid="selected-checkbox" />
              ) : (
                <RadioUnCheckedIcon data-testid="unselected-checkbox" />
              )}
            </div>
            <Typography className="typo-text body3_size list-text">
              {params.value}
            </Typography>
          </div>
        ),
      },
    ];
    if (selectedListType === 'EVIDENCE_SOURCE') {
      columns.push({
        field: 'custodian',
        headerName: t(
          'reports.processingInProgress.evidenceSourceDialog.header.custodian'
        ),
        flex: 0.75,
        sortable: false,
        renderCell: (params) => (
          <Typography className="typo-text body3_size list-text">
            {params.value}
          </Typography>
        ),
      });
    }
    return columns;
  }, [selectedListType, selectedRowItem]);

  const focusContinueButton = () => {
    setTimeout(() => {
      const button = document.querySelector(
        '[data-testid="pagination-continue-button"]'
      );
      if (button) {
        (button as HTMLButtonElement).focus();
      }
    }, 200);
  };

  const fetchEvidenceSources = async (searchTerm?: string) => {
    const {selectedProject, selectedWorkspace, noOfItemPerPage, currentPageNo} =
      updatedStateRef.current;
    if (selectedProject && selectedWorkspace && clientId) {
      try {
        setValue(
          searchTerm
            ? ActionType.SET_EVIDENCE_SEARCH_LOADING
            : ActionType.START_EVIDENCE_LOADING,
          true
        );
        const queryParam = {
          clientId: clientId,
          projectId: selectedProject.id,
          pageSize: noOfItemPerPage.toString(),
          pageNumber: currentPageNo.toString(),
          SearchTerm: '',
        };
        if (searchTerm) {
          queryParam.SearchTerm = searchTerm;
        }

        const response = await getInprogressEvidenceSourcesByWorkspaceId(
          selectedWorkspace.id,
          new URLSearchParams(queryParam).toString()
        );
        const evidenceSources = _.get(response, 'data.data', []);

        const totalItem = _.get(response, 'data.meta.pagination.totalItems', 0);

        evidenceLoadedForWorkspaceId.current = selectedWorkspace.id;
        setValue(ActionType.STORE_EVIDENCE_RESPONSE, {
          evidences: evidenceSources,
          totalItemCount: totalItem,
        });
      } catch (err: any) {
        setValue(ActionType.SET_EVIDENCE_REQUEST_ERROR, {
          data: {...err.data, status: err.status},
        });
      }
    }
  };

  const handleRowSelection = useCallback(
    (param: GridRowParams) => {
      // Preventing selected event when row is already selected
      if (selectedRowItem?.id !== param.id)
        switch (selectedListType) {
          case 'PROJECT':
            setValue(ActionType.SET_SELECTED_PROJECT, param.row);
            break;
          case 'WORKSPACE':
            setValue(ActionType.SET_SELECTED_WORKSPACE, param.row);
            break;
          case 'EVIDENCE_SOURCE':
            setValue(ActionType.SET_SELECTED_EVIDENCE, param.row);
            focusContinueButton();
            break;
          default:
            break;
        }
    },
    [selectedListType, setValue, selectedRowItem]
  );

  const debounceSearchEvent = useCallback(
    _.debounce((input: string) => {
      fetchEvidenceSources(input);
    }, 1000),
    [
      selectedProject,
      selectedWorkspace,
      clientId,
      noOfItemPerPage,
      currentPageNo,
    ]
  );

  const handleOnSearchChange = useCallback(
    (input: string) => {
      setValue(ActionType.SET_SEARCH_TERM, input);
      if (updatedStateRef.current.selectedListType === 'EVIDENCE_SOURCE')
        debounceSearchEvent(input);
    },
    [setValue]
  );

  const handleCurrentPageChange = useCallback(
    (pageNo: number) => {
      setValue(ActionType.SET_CURRENT_PAGE_NO, pageNo);
    },
    [setValue]
  );

  const handlePerPageChange = useCallback(
    (perPageNo: number) => {
      setValue(ActionType.SET_NO_OF_ITEM_PAGE_PAGE, perPageNo);
    },
    [setValue]
  );

  const handleRefreshClick = useCallback(() => {
    switch (selectedListType) {
      case 'EVIDENCE_SOURCE':
        fetchEvidenceSources(searchQuery);
        break;
      default:
        break;
    }
  }, [selectedListType, searchQuery]);

  const handleContinueClick = useCallback(() => {
    if (selectedEvidenceSource && selectedWorkspace && selectedProject) {
      dispatch(
        setMediaEvidenceId({
          mediaId: selectedEvidenceSource.id,
          mediaName: selectedEvidenceSource.name,
          projectId: selectedProject.id,
          projectName: selectedProject.name,
          workspaceId: selectedWorkspace.id,
          workspaceName: selectedWorkspace.name,
        })
      );
      setValue(ActionType.RESET_DATA_ON_CANCEL);
      evidenceLoadedForWorkspaceId.current = null;
    }
    setValue(ActionType.SET_SEARCH_TERM, '');

    trackGAEvent({
      eventName: GA_EVENT_NAMES.CLICK,
      click: {
        name: t('gaTracking.global.continue'),
        type: GA_CLICK_TYPES.BUTTON,
        region: GA_REGIONS.MODAL,
        action: t('gaTracking.reports.evidenceSourceDialog.title'),
      },
      subPage: {
        subpage_name: t('gaTracking.reports.evidenceSourceDialog.title'),
        subpage_type: GA_SUBPAGE_TYPE.MODAL,
      },
    });
    onContinue();
  }, [selectedEvidenceSource, selectedWorkspace, selectedProject]);

  const handleCloseClick = useCallback(() => {
    setValue(ActionType.SET_SEARCH_TERM, '');
    setValue(ActionType.RESET_DATA_ON_CANCEL);
    evidenceLoadedForWorkspaceId.current = null;
    onClose();
  }, [previousReportTypeTab, mediaEvidenceId]);

  const renderHeader = () => {
    return (
      <div className="header">
        <Typography className="typo-text headline3_size title-text">
          {t('reports.processingInProgress.evidenceSourceDialog.title')}
        </Typography>
        <Typography className="typo-text body2_size sub-title-text">
          {t('reports.processingInProgress.evidenceSourceDialog.subTitle')}
        </Typography>
      </div>
    );
  };

  const renderSelectionButtons = () => {
    const renderTextField = (
      type: SelectedListType,
      label: string,
      value?: string,
      testId?: string,
      ref?: MutableRefObject<HTMLInputElement | null>,
      disabled = false
    ) => {
      let tabIndex = 0;
      switch (type) {
        case 'WORKSPACE':
          if (selectedListType === 'PROJECT') {
            /**
             * When Project button selected so next focusable element is workspace button as per sequance in dom
             * that's why set tabIndex = -1 to disable focus of workspace button from keyboard tab press event and send tab
             * event to next active element datagrid in node.
             */
            tabIndex = !focusedTextField ? 0 : -1;
          }
          break;
        case 'EVIDENCE_SOURCE':
          /**
           * When Project/Workspace button selected so next focusable element is evidence button as per sequance in dom
           * that's why set tabIndex = -1 to disable focus of Project/Workspace button from keyboard tab press event and send tab
           * event to next active element datagrid in node.
           */
          if (
            selectedListType === 'WORKSPACE' ||
            selectedListType === 'PROJECT'
          )
            tabIndex = !focusedTextField ? 0 : -1;
          break;
      }
      return (
        <AurTextField
          label={t(
            `reports.processingInProgress.evidenceSourceDialog.${label}`
          )}
          ariaLabel={t(
            `reports.processingInProgress.evidenceSourceDialog.${label}`
          )}
          variant="filled"
          placeholder={t(
            'reports.processingInProgress.evidenceSourceDialog.chooseFromList'
          )}
          InputProps={{
            readOnly: true, // Make the input read-only
            disableUnderline: true,
            tabIndex: -1, // disabled outer div of input field by set -1
          }}
          inputProps={{
            tabIndex: tabIndex,
            id: testId,
            ref: ref,
          }}
          value={value ?? ''}
          onFocus={() => {
            setFocusedTextField(type);
            setSelectedListType(type);
          }}
          onBlur={() => setFocusedTextField(null)}
          className={`aur-textfield custom-text-field ${selectedListType === type ? 'focused' : ''}`}
          disabled={disabled}
          data-testid={testId}
          tabIndex={-1} // disabled outer div of input field by set -1
        />
      );
    };
    return (
      <div className="selection-buttons-row" tabIndex={-1}>
        {renderTextField(
          'PROJECT',
          'selectProject',
          selectedProject?.name,
          'project-button',
          projectButtonRef
        )}
        {renderTextField(
          'WORKSPACE',
          'selectWorkspace',
          selectedWorkspace?.name,
          'workspace-button',
          workspaceButtonRef
        )}
        {renderTextField(
          'EVIDENCE_SOURCE',
          'selectMediaEvidenceSet',
          selectedEvidenceSource?.name,
          'evidence-source-button',
          evidenceButtonRef,
          selectedWorkspace === undefined
        )}
      </div>
    );
  };

  const renderDataGridOveraly = () => {
    if (reqLoading) return AurDataGridLoadingOverlay;

    if (!haveReqError) return AurDataGridNoRowsOverlay;
    return EmptyDiv;
  };

  const renderTable = () => {
    return (
      <>
        <Box className="search-text-box-container">
          <Box>
            <AurSearchBox
              label={t('common.search')}
              onSearch={handleOnSearchChange}
              className="search-text-box"
              value={searchQuery}
            />
          </Box>
        </Box>
        <div
          className={`table-container ${!haveReqError ? 'flex-one' : ''}`}
          ref={tableContainerRef}
        >
          <AurDataGrid
            key={`key-${noOfItemPerPage}`}
            className={`listing-data-table ${haveReqError && 'listing-data-table-fix-height'}`}
            rows={tableRows}
            columns={tableColumns}
            visibleToolTip={true}
            pageSizeOptions={perPageOptions}
            entityType={t('common.selectionList')}
            pointer
            currentPage={currentPageNo}
            setCurrentPage={handleCurrentPageChange}
            itemsPerPage={noOfItemPerPage}
            setItemsPerPage={handlePerPageChange}
            totalRowCount={totalRowCount}
            searchLoading={evidenceSearchLoading}
            showPagination={false}
            checkboxSelection={false}
            getRowId={(row) => row.id}
            selectedIds={selectedRowItem ? [selectedRowItem.id] : []}
            getRowHeight={() => ROW_HEIGHT}
            columnHeaderHeight={COLUMN_HEIGHT}
            onRowClick={handleRowSelection}
            onSearch={handleOnSearchChange}
            searchValue={searchQuery}
            noRowsOverlay={renderDataGridOveraly()}
            aria-label={getTableAriaLabel(selectedListType)}
            aria-live="polite"
            headerSpanTabIndex={-1}
            defaultHeaderCellTabIndex={-1}
            enableRowClickOnEnterPress
            disableHeaderRowTabIndex
            addedInDialog
            classNameOfParentScollElement="table-container"
          />
        </div>
      </>
    );
  };

  const renderReqError = () => {
    if (haveReqError)
      return (
        <div className="table-error-container">
          <AurDataGridErrorOverlay
            onRefresh={handleRefreshClick}
            enableParentWrapper={false}
          />
        </div>
      );
    return null;
  };

  const renderActions = () => {
    let continueHint = t(COMMON_STRING_PATH + 'accessibility.continueButton');
    if (!enableContinueButton)
      continueHint = t(
        COMMON_STRING_PATH + 'accessibility.makeContinueButtonEnable'
      );
    return (
      <DialogActions className="footer-action-container">
        <AurPagination
          totalItems={totalRowCount}
          itemsPerPage={noOfItemPerPage}
          currentPage={currentPageNo}
          perPageOptions={perPageOptions}
          itemType={t('common.selectionList')}
          setItemsPerPage={handlePerPageChange}
          onPageChange={handleCurrentPageChange}
          includeCancelContinueEvents
          handleCancelClick={handleCloseClick}
          handleContinueClick={handleContinueClick}
          continuButtonDisabled={!enableContinueButton}
          backgroundColor="#0C1119"
          continueButtonHint={continueHint}
          canClearAriaLabel={t(
            COMMON_STRING_PATH + 'accessibility.cancelButton',
            {
              screen:
                !mediaEvidenceId && !previousReportTypeTab
                  ? t(COMMON_STRING_PATH + 'accessibility.welcomeClient')
                  : t(
                      COMMON_STRING_PATH +
                        'accessibility.processingInProgressTab'
                    ),
            }
          )}
          trackPaginationActionName={t('gaTracking.global.reportPagination')}
        />
      </DialogActions>
    );
  };

  return (
    <Dialog
      fullScreen={fullScreen}
      open={isOpen}
      onClose={handleCloseClick}
      maxWidth={false}
      disablePortal={disablePortal}
      PaperProps={{
        className: 'evidence-source-dialog',
        role: 'dialog',
        'aria-label': t(
          'reports.processingInProgress.evidenceSourceDialog.title'
        ),
        'aria-labelledby': t(
          'reports.processingInProgress.evidenceSourceDialog.title'
        ),
        'aria-describedby': t(
          'reports.processingInProgress.evidenceSourceDialog.subTitle'
        ),
      }}
    >
      <DialogContent className="model-content">
        <>
          {renderHeader()}
          {renderSelectionButtons()}
          {renderTable()}
          {renderReqError()}
        </>
      </DialogContent>
      {renderActions()}
    </Dialog>
  );
};

export default EvidenceSelectionDialog;
